import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Typography,
  alpha,
} from '@mui/material';
import { ErrorDialog } from '@core/components/dialogs';
import DropdownChip from '@core/components/ui/DropdownChip';
import { useMenu } from '@core/hooks/useMenu';
import { useProgressiveImageSrc } from '@core/hooks/useProgressiveImageSrc';
import { getLabelFromId } from '@core/utils/collection';
import { getValues } from '@core/utils/enum';
import { toProperCase } from '@core/utils/string';
import type {
  AlbItem,
  OrderItem as ApiOrderItem,
  ChasubleItem,
  StoleItem,
} from '../../../api/order';
import { updateProductionItemStage } from '../../../api/productionItems';
import { SummarySegments } from '../../components/SummarySegments';
import { useAppContext } from '../../context/AppContext';
import { ORDER_STATUS_PALETTE, OrderStatus } from '../../enum/orderStatus';
import { ProductionStage } from '../../enum/productionStage';
import { getCategory, isV1VestmentType } from '../../enum/vestmentType';
import { getAlbFeatures, getAlbPreviewSubtype } from '../../utils/alb';
import { getImageThumbnailUrl } from '../../utils/image';
import { isActiveProdOrder } from '../../utils/order';
import { ORDER_ITEM_REGISTRY } from '../../utils/orderItem/registry';
import type { OrderItemSummarySegment } from '../../utils/orderItem/types';
import {
  VESTMENT_CATEGORY_IMAGES,
  getVestmentImageUrls,
} from '../../utils/vestment';

interface OrderDetailItemProps {
  item: ApiOrderItem;
  orderStatus: OrderStatus;
  readOnly?: boolean;
  onUpdated?: () => void | Promise<void>;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}

const OrderDetailItem = ({
  item,
  orderStatus,
  readOnly = false,
  onUpdated,
  onEdit,
  onDuplicate,
  onDelete,
}: OrderDetailItemProps) => {
  const {
    vestments,
    sizes,
    collars,
    fabrics,
    threads,
    trimmings,
    galoons,
    cords,
    tassels,
    colors,
  } = useAppContext();

  const { anchorEl, isOpen, openMenu, closeMenu } = useMenu();
  const [missingFieldsOpen, setMissingFieldsOpen] = useState(false);

  const summarySegments = useMemo((): OrderItemSummarySegment[] => {
    const type = item.vestment?.type;
    if (!type) return [];
    const category = getCategory(type);
    const config = ORDER_ITEM_REGISTRY[category];
    const formValues = config.apiToFormValues(item);
    return config.formatSummarySegments(formValues, {
      customer: item.forCustomer,
      vestments,
      sizes,
      collars,
      fabrics,
      threads,
      trimmings,
      galoons,
      cords,
      tassels,
      colors,
    });
  }, [
    item,
    vestments,
    sizes,
    collars,
    fabrics,
    threads,
    trimmings,
    galoons,
    cords,
    tassels,
    colors,
  ]);

  const missingFieldSections = useMemo(() => {
    const messages = Object.values(item.missingRequiredFields ?? {});
    if (messages.length === 0) {
      return [];
    }
    return [{ title: `Line item #${item.id}`, messages }];
  }, [item.id, item.missingRequiredFields]);

  const vestmentName = getLabelFromId(
    vestments,
    String(item.vestment?.id ?? '')
  );
  const vestmentType = item.vestment?.type;
  const showsTitleDesignCode =
    !!item.design?.code &&
    !(
      vestmentType &&
      getAlbFeatures(vestmentType) &&
      !isV1VestmentType(vestmentType)
    );
  const catalogVestment = vestments.find(
    (vestment) => vestment.id === item.vestment?.id
  );
  const designThumbnailUrl = getImageThumbnailUrl(item.design);
  const vestmentImages = designThumbnailUrl
    ? null
    : getVestmentImageUrls(
        item.vestment,
        (item as ChasubleItem).collar,
        (item as StoleItem).stoleCut,
        getAlbPreviewSubtype(
          catalogVestment?.albSubtypes,
          (item as AlbItem).subtype,
          (item as AlbItem).laceStyle
        )
      );
  const vestmentPreviewUrl = useProgressiveImageSrc(
    vestmentImages?.thumbnailUrl ?? null,
    vestmentImages?.imageUrl ?? null
  );
  const thumbnailUrl =
    designThumbnailUrl ??
    vestmentPreviewUrl ??
    VESTMENT_CATEGORY_IMAGES[getCategory(item.vestment.type)];
  const isEditable = !readOnly && !!onEdit;
  const stage = item.productionStage?.trim();
  const isKnownStage = !!stage && getValues(ProductionStage).includes(stage);
  const showProgress = Boolean(isActiveProdOrder(orderStatus) && isKnownStage);
  const progress = item.productionProgress ?? 0;
  const progressPalette =
    stage === ProductionStage.Completed
      ? ORDER_STATUS_PALETTE[OrderStatus.ReadyForRelease]
      : ORDER_STATUS_PALETTE[OrderStatus.InProduction];

  const handleItemStageChange = useCallback(
    async (itemId: number, nextStage: ProductionStage) => {
      if (stage === nextStage) return;
      try {
        await updateProductionItemStage(itemId, nextStage);
        await onUpdated?.();
      } catch {
        toast.error('Failed to update production stage');
      }
    },
    [onUpdated, stage]
  );

  const handleMenuAction = (action: () => void) => {
    closeMenu();
    action();
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.12),
        transition: 'border-color 0.2s ease',
        ...(isEditable && {
          '&:hover': {
            borderColor: 'primary.main',
          },
        }),
      }}
    >
      <Box display="flex" alignItems="stretch" gap={2}>
        <Box
          onClick={isEditable ? () => onEdit?.() : undefined}
          sx={{
            width: 100,
            height: 100,
            flexShrink: 0,
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'action.hover',
            display: 'grid',
            placeItems: 'center',
            ...(isEditable && { cursor: 'pointer' }),
          }}
        >
          <Box
            component="img"
            src={thumbnailUrl}
            loading="lazy"
            decoding="async"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              onClick={isEditable ? () => onEdit?.() : undefined}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
                flex: 1,
                minWidth: 0,
                ...(isEditable && { cursor: 'pointer' }),
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.quantity ?? 1} {vestmentName}
                {showsTitleDesignCode ? ` (${item.design?.code})` : ''}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0 }}
              >
                # {item.id}
              </Typography>
            </Box>
            {/* {Object.keys(item.missingRequiredFields ?? {}).length > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => setMissingFieldsOpen(true)}
                sx={{
                  minWidth: 0,
                  p: 0,
                  font: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  textTransform: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    bgcolor: 'transparent',
                  },
                }}
              >
                Draft
              </Button>
            )} */}
            {!readOnly ? (
              <>
                <IconButton size="small" onClick={openMenu} sx={{ p: 0.5 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu anchorEl={anchorEl} open={isOpen} onClose={closeMenu}>
                  <MenuItem onClick={() => handleMenuAction(() => onEdit?.())}>
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMenuAction(() => onDuplicate?.())}
                  >
                    Duplicate
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMenuAction(() => onDelete?.())}
                    sx={{ color: 'error.main' }}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </>
            ) : null}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 2,
              flex: 1,
            }}
          >
            <Box
              onClick={isEditable ? () => onEdit?.() : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                alignSelf: 'flex-start',
                ...(isEditable && { cursor: 'pointer' }),
              }}
            >
              <SummarySegments segments={summarySegments} />

              {item.notes?.trim() ? (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontStyle: 'italic' }}
                >
                  {item.notes}
                </Typography>
              ) : null}
            </Box>

            {showProgress && stage ? (
              <Box sx={{ width: 230, flexShrink: 0 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <DropdownChip
                    label={toProperCase(stage)}
                    color={progressPalette}
                    options={getValues(ProductionStage)
                      .filter((option) => option !== stage)
                      .map((option) => ({
                        value: option,
                        label: toProperCase(option),
                      }))}
                    onChange={(nextStage) =>
                      handleItemStageChange(
                        item.id,
                        nextStage as ProductionStage
                      )
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {progress}% completed
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    mt: 0.75,
                    height: 4,
                    borderRadius: 999,
                    bgcolor: `${progressPalette}.200`,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: `${progressPalette}.800`,
                    },
                  }}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      <ErrorDialog
        open={missingFieldsOpen}
        title="Incomplete line item"
        sections={missingFieldSections}
        onClose={() => setMissingFieldsOpen(false)}
      />
    </Box>
  );
};

export default OrderDetailItem;
